import { useState, useEffect, useRef, useCallback, forwardRef } from 'react';
import { cn } from '@/lib/utils';
import { generatePlaceholderDataUri } from '@/lib/image/placeholder';
import { isIpfsUrl, getAlternateGateways } from '@/lib/image/ipfs';
import { addBreadcrumb } from '@/lib/sentry';

interface FadeImageProps extends React.ComponentPropsWithoutRef<'img'> {
  fallbackSeed?: string;
  fallbackSrc?: string;
}

const RETRY_DELAYS = [2000, 4000, 8000];

export const FadeImage = forwardRef<HTMLImageElement, FadeImageProps>(
  function FadeImage({ fallbackSeed, fallbackSrc, src, className, onLoad, onError, ...props }, ref) {
    const [displaySrc, setDisplaySrc] = useState<string | undefined>(src);
    const [loaded, setLoaded] = useState(false);
    const [showingPlaceholder, setShowingPlaceholder] = useState(false);

    const gatewayIndexRef = useRef(0);
    const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const mountedRef = useRef(true);

    // Build the list of URLs to try (original + IPFS gateways)
    const getAllUrls = useCallback((originalSrc: string): string[] => {
      if (isIpfsUrl(originalSrc)) {
        return [originalSrc, ...getAlternateGateways(originalSrc)];
      }
      return [originalSrc];
    }, []);

    // Local placeholder data URI
    const placeholderUri = fallbackSrc
      || (fallbackSeed ? generatePlaceholderDataUri(fallbackSeed) : undefined);

    // Reset state when src changes
    useEffect(() => {
      setDisplaySrc(src);
      setLoaded(false);
      setShowingPlaceholder(false);
      gatewayIndexRef.current = 0;
      if (retryTimerRef.current) {
        clearTimeout(retryTimerRef.current);
        retryTimerRef.current = undefined;
      }
    }, [src]);

    // Cleanup on unmount
    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
        if (retryTimerRef.current) {
          clearTimeout(retryTimerRef.current);
        }
      };
    }, []);

    // Background retry: cycle through all gateway URLs
    const startBackgroundRetry = useCallback((originalSrc: string) => {
      const urls = getAllUrls(originalSrc);
      let attempt = 0;

      const tryNext = () => {
        if (!mountedRef.current || attempt >= RETRY_DELAYS.length) return;

        const delay = RETRY_DELAYS[attempt];
        const urlIndex = attempt % urls.length;
        const url = urls[urlIndex];
        attempt++;

        retryTimerRef.current = setTimeout(() => {
          if (!mountedRef.current) return;

          const img = new Image();
          img.onload = () => {
            if (mountedRef.current) {
              setDisplaySrc(url);
              setShowingPlaceholder(false);
              setLoaded(true);
            }
          };
          img.onerror = () => {
            tryNext();
          };
          img.src = url;
        }, delay);
      };

      tryNext();
    }, [getAllUrls]);

    const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
      setLoaded(true);
      onLoad?.(e);
    };

    const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
      const currentSrc = displaySrc || '';
      const isIpfs = isIpfsUrl(currentSrc);

      addBreadcrumb('Image load failed', 'image', {
        url: currentSrc,
        isIpfs,
        gatewayIndex: gatewayIndexRef.current,
      });

      // Try next IPFS gateway
      if (isIpfs && src) {
        const urls = getAllUrls(src);
        gatewayIndexRef.current++;

        if (gatewayIndexRef.current < urls.length) {
          setDisplaySrc(urls[gatewayIndexRef.current]);
          onError?.(e);
          return;
        }
      }

      // All gateways exhausted (or non-IPFS): show placeholder
      if (placeholderUri) {
        setDisplaySrc(placeholderUri);
        setShowingPlaceholder(true);
        setLoaded(true);
      } else {
        setLoaded(true); // Show whatever we have
      }

      // Start background retry on the original src
      if (src) {
        startBackgroundRetry(src);
      }

      onError?.(e);
    };

    return (
      <img
        ref={ref}
        loading="lazy"
        src={displaySrc}
        className={cn(
          className,
          loaded ? 'opacity-100' : 'opacity-0',
          'transition-opacity duration-200',
        )}
        onLoad={handleLoad}
        onError={handleError}
        {...props}
      />
    );
  }
);
